import React, { useEffect, useState } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import axios from "axios";
import "bootstrap/dist/css/bootstrap.min.css";
import "bootstrap-icons/font/bootstrap-icons.css";
import "./ComplaintsDetails.css";
import { HiOutlineThumbUp, HiOutlineThumbDown } from "react-icons/hi";
import { IoMdSend } from "react-icons/io";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { BiCategoryAlt } from "react-icons/bi";
import { FaDoorOpen } from "react-icons/fa";
import { FiWifi, FiClock } from "react-icons/fi";
import { IoMdCall } from "react-icons/io";
import { useAuth } from "../../Context/AuthContext";
import ComplaintCategoryWithFlag from "../ComplaintCategoryWithFlag/ComplaintCategoryWithFlag";
import { isExperimental } from "../../utils/isExperimental";
import ReassignmentProtectionModal from "../ReassignmentProtectionModal/ReassignmentProtectionModal";
import { FaUser } from "react-icons/fa";

// Validates that text has at least `minWords` and is not repeated spam
const isValidComment = (text, minWords = 4) => {
  if (!text) return false;
  const words = text.trim().match(/\b[a-zA-Z0-9'-]+\b/g) || [];
  if (words.length < minWords) return false;

  // Prevent spam like "good good good good" or "ok ok ok ok"
  const uniqueWords = new Set(words.map((w) => w.toLowerCase()));
  if (uniqueWords.size < 2) return false;

  return true;
};

const ComplaintsDetails = () => {
  const { complaint_id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const [complaint, setComplaint] = useState(null);
  const [leaveCheckTime, setLeaveCheckTime] = useState(Date.now());
  const [status, setStatus] = useState("");
  const [newComment, setNewComment] = useState("");
  const { user, isAdmin, isAssistant } = useAuth();
  const baseUrl = process.env.REACT_APP_COMPLAINTS_APP_BE_URL;
  const [showReassignmentModal, setShowReassignmentModal] = useState(false);
  const [reassignmentCheck, setReassignmentCheck] = useState({ isReassigned: false, currentUser: null, assignedTo: null });
  const [userVotes, setUserVotes] = useState({});
  const isStudent = !isAdmin && !isAssistant;
  const adminLeaveEndTime = complaint?.adminLeaveToDate
    ? new Date(complaint.adminLeaveToDate).getTime()
    : null;
  const showAdminLeaveNotice =
    isStudent &&
    complaint?.adminOnLeave &&
    Number.isFinite(adminLeaveEndTime) &&
    adminLeaveEndTime >= leaveCheckTime;

  // Modal state for mandatory resolution
  const [showResolutionModal, setShowResolutionModal] = useState(false);
  const [resolutionText, setResolutionText] = useState("");
  const [resolutionError, setResolutionError] = useState("");
  const [isSubmittingResolution, setIsSubmittingResolution] = useState(false);

  // determine if complaint is flagged (used to disable updates)
  const isFlagged = complaint?.flagged === true || complaint?.flagged?.isFlagged === true;
  
  // Check if complaint is reassigned to another assistant (for current assistant user)
  const isReassigned =
    isAssistant &&
    complaint?.assignedAssistant &&
    complaint?.assignedAssistant !== user?.email;
  const isTakenBack =
    isAssistant && !complaint?.assignedAssistant;

  const isNotAllowed = isTakenBack || isReassigned;

  // Track expanded replies per comment (Complaint Details page)
  const [expandedReplies, setExpandedReplies] = useState({});

  const toggleReplies = (commentId) => {
    setExpandedReplies((prev) => ({
      ...prev,
      [commentId]: !prev[commentId],
    }));
  };

  const visibleComments = complaint?.comments?.filter(
    (c) =>
      c.text &&
      !c.text.includes("[Assistant Update]") &&
      !c.text.toLowerCase().includes("updated status to")
  ) || [];

  const formatDate = (timestamp) => {
    if (!timestamp) return "";

    const date = new Date(timestamp);
    if (isNaN(date.getTime())) return "";

    const now = new Date();

    const timePart = date.toLocaleTimeString("en-US", {
      hour: "numeric",
      minute: "2-digit",
      hour12: true,
    });

    const startOfToday = new Date(
      now.getFullYear(),
      now.getMonth(),
      now.getDate()
    );

    const startOfDate = new Date(
      date.getFullYear(),
      date.getMonth(),
      date.getDate()
    );

    const diffDays = Math.floor(
      (startOfToday - startOfDate) / (1000 * 60 * 60 * 24)
    );

    if (diffDays === 0) return `Today, ${timePart}`;
    if (diffDays === 1) return `Yesterday, ${timePart}`;
    if (diffDays >= 2 && diffDays <= 6) return `${diffDays} days ago`;
    if (diffDays >= 7 && diffDays <= 14) return "1 week ago";
    if (diffDays >= 15 && diffDays <= 21) return "2 weeks ago";
    if (diffDays >= 22 && diffDays <= 28) return "3 weeks ago";
    if (diffDays >= 29) {
      const months = Math.floor(diffDays / 30);
      return months === 1 ? "1 month ago" : `${months} months ago`;
    }

    return `${diffDays} days ago`;
  };

  const handleVote = async (id, type) => {
    const prevVote = userVotes[id];
    try {
      const token = localStorage.getItem("authToken");
      const url = `${baseUrl}/user-api/${type === "upvote" ? "like" : "dislike"}-complaint/${id}`;
      await axios.post(
        url,
        { email: user?.email },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      setUserVotes((prevVotes) => {
        if (prevVote === type) {
          const updatedVotes = { ...prevVotes };
          delete updatedVotes[id];
          return updatedVotes;
        } else {
          return { ...prevVotes, [id]: type };
        }
      });

      setComplaint((prev) => {
        if (!prev || prev.complaint_id !== id) return prev;

        let likes = prev.likes;
        let dislikes = prev.dislikes;

        if (prevVote === "upvote") likes = Math.max(0, likes - 1);
        if (prevVote === "downvote") dislikes = Math.max(0, dislikes - 1);

        if (prevVote !== type) {
          if (type === "upvote") {
            likes++;
          } else {
            dislikes++;
          }
        }

        return {
          ...prev,
          likes,
          dislikes,
        };
      });
    } catch (err) {
      console.error("Error updating vote:", err);
    }
  };

  useEffect(() => {
    const fetchComplaint = async () => {
      try {
        const token = localStorage.getItem("authToken");

        const response = await axios.get(
          `${baseUrl}/admin-api/view-complaint/${complaint_id}`,
          {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          }
        );

        const complaintData = response.data.complaint;
        setComplaint({
          ...complaintData,
          comments: complaintData.comments || [],
        });
        setStatus(complaintData.status);
        if (complaintData.userVote) {
          setUserVotes({
            [complaintData.complaint_id]: complaintData.userVote,
          });
        }
        
        if (isAssistant) {
          if (!complaintData.assignedAssistant) {
            toast.error("This complaint was taken back by Admin.");
          } else if (complaintData.assignedAssistant !== user?.email) {
            toast.error(
              `This complaint is now assigned to ${complaintData.assignedAssistant}.`
            );
          }
        }
      } catch (error) {
        console.error("Error fetching complaint details:", error);
      }
    };
    fetchComplaint();
  }, [complaint_id, isAssistant, user?.email]);

  useEffect(() => {
    if (!complaint?.adminOnLeave) return undefined;
    const timer = setInterval(() => setLeaveCheckTime(Date.now()), 60 * 1000);
    return () => clearInterval(timer);
  }, [complaint?.adminOnLeave]);

  const handleStatusChange = (e) => {
    const updatedStatus = e.target.value;

    if (!user || !user.email) {
      toast.error("User email missing. Please login again.");
      return;
    }

    if (isAssistant && complaint?.assignedAssistant && complaint.assignedAssistant !== user.email) {
      setReassignmentCheck({
        isReassigned: true,
        currentUser: user.email,
        assignedTo: complaint.assignedAssistant
      });
      setShowReassignmentModal(true);
      setStatus(complaint.status);
      return;
    }

    // Intercept if status is Resolved
    if (updatedStatus === "Resolved") {
      setResolutionText("");
      setResolutionError("");
      setShowResolutionModal(true);
      return;
    }

    // Otherwise directly update status for other values
    submitStatusUpdate(updatedStatus);
  };

  const submitStatusUpdate = async (newStatus, reason = "") => {
    try {
      const token = localStorage.getItem("authToken");

      if (isAssistant) {
        await axios.put(
          `${baseUrl}/admin-api/update-complaint-status-assistant`,
          {
            complaintId: complaint_id,
            status: newStatus,
            assistantEmail: user.email,
            remarks: reason || `Status updated to ${newStatus} by assistant`
          },
          { headers: { Authorization: `Bearer ${token}` } }
        );
      } else {
        await axios.put(
          `${baseUrl}/admin-api/update-status/${complaint_id}`,
          { status: newStatus, adminEmail: user.email },
          { headers: { Authorization: `Bearer ${token}` } }
        );
      }

      if (newStatus === "Resolved" && reason) {
        const commentText = `[Resolved Solution]: ${reason}`;
        await axios.post(
          `${baseUrl}/admin-api/complaints/${complaint_id}/comment`,
          {
            text: commentText,
            adminEmail: user.email,
          },
          {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          }
        );

        setComplaint((prev) => ({
          ...prev,
          status: newStatus,
          comments: [
            ...(prev.comments || []),
            {
              id: new Date().getTime(),
              text: commentText,
              date: new Date().toISOString(),
              email: user.email,
            },
          ],
        }));
      } else {
        setComplaint((prev) => ({ ...prev, status: newStatus }));
      }

      setStatus(newStatus);
      toast.success(`Status updated to: ${newStatus}`);
      if (isExperimental) toast.info("📧 Demo: Status update email simulated.");
    } catch (error) {
      console.error("Error updating status:", error);
      setStatus(complaint.status);
      
      if (error.response?.status === 403 && error.response?.data?.message === "Complaint not assigned to you") {
        try {
          const token = localStorage.getItem("authToken");
          const response = await axios.get(
            `${baseUrl}/admin-api/view-complaint/${complaint_id}`,
            { headers: { Authorization: `Bearer ${token}` } }
          );
          const updatedComplaint = response.data.complaint;
          
          setReassignmentCheck({
            isReassigned: true,
            currentUser: user.email,
            assignedTo: updatedComplaint.assignedAssistant || "Unknown"
          });
          setShowReassignmentModal(true);
        } catch (fetchError) {
          console.error("Error fetching updated complaint:", fetchError);
          toast.error("This complaint has been reassigned to another assistant.");
        }
      } else {
        toast.error("Failed to update status. You may not be assigned to this complaint.");
      }
    }
  };

  const handleConfirmResolution = async () => {
    const trimmed = resolutionText.trim();

    if (!trimmed) {
      setResolutionError("Please provide what action was taken to resolve this problem.");
      return;
    }

    if (!isValidComment(trimmed, 4)) {
      setResolutionError("Please enter a meaningful resolution summary (at least 4 words).");
      return;
    }

    setIsSubmittingResolution(true);
    await submitStatusUpdate("Resolved", trimmed);
    setIsSubmittingResolution(false);
    setShowResolutionModal(false);
    setResolutionText("");
  };

  const handleCancelResolution = () => {
    setShowResolutionModal(false);
    setResolutionText("");
    setResolutionError("");
    setStatus(complaint.status);
  };

  const handleCategoryChange = async (e) => {
    const newCategory = e.target.value;

    if (newCategory === complaint.category) {
      toast.info("Please select a different category");
      return;
    }

    if (
      !window.confirm(
        `Are you sure you want to move this complaint to ${newCategory}?`
      )
    ) {
      return;
    }

    try {
      const token = localStorage.getItem("authToken");

      await axios.put(
        `${baseUrl}/admin-api/change-category/${complaint_id}`,
        {
          newCategory,
          adminEmail: user.email,
        },
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      setComplaint((prev) => ({ ...prev, category: newCategory }));
      toast.success(`Complaint moved to ${newCategory}.`);
    } catch (error) {
      console.error("Error changing category:", error);
      toast.error("Failed to change category.");
    }
  };

  const handleCommentSubmit = async () => {
    const trimmedComment = newComment.trim();

    if (!trimmedComment) return;

    if (!isValidComment(trimmedComment, 4)) {
      toast.warning("Please enter a meaningful comment with at least 4 words.");
      return;
    }
    
    if (isNotAllowed) {
      setReassignmentCheck({
        isReassigned: true,
        currentUser: user.email,
        assignedTo: complaint.assignedAssistant
      });
      setShowReassignmentModal(true);
      return;
    }

    try {
      const token = localStorage.getItem("authToken");

      await axios.post(
        `${baseUrl}/admin-api/complaints/${complaint_id}/comment`,
        {
          text: trimmedComment,
          adminEmail: user.email,
        },
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      setComplaint((prev) => ({
        ...prev,
        comments: [
          ...(prev.comments || []),
          {
            id: new Date().getTime(),
            text: trimmedComment,
            date: new Date().toISOString(),
            email: user.email,
          },
        ],
      }));

      setNewComment("");
      if (isExperimental) toast.info("📧 Demo: New comment email simulated.");
    } catch (error) {
      console.error("Error adding comment:", error);
      if (error.response?.status === 403) {
        toast.error("You cannot add comments. This complaint has been reassigned to another assistant.");
        try {
          const token = localStorage.getItem("authToken");
          const response = await axios.get(
            `${baseUrl}/admin-api/view-complaint/${complaint_id}`,
            { headers: { Authorization: `Bearer ${token}` } }
          );
          const updatedComplaint = response.data.complaint;
          
          setReassignmentCheck({
            isReassigned: true,
            currentUser: user.email,
            assignedTo: updatedComplaint.assignedAssistant || "Unknown"
          });
          setShowReassignmentModal(true);
        } catch (fetchError) {
          console.error("Error fetching updated complaint:", fetchError);
        }
      } else {
        toast.error("Failed to add comment.");
      }
    }
  };

  const handleDeleteComplaint = async () => {
    if (!window.confirm("Are you sure you want to delete this complaint?"))
      return;

    try {
      const token = localStorage.getItem("authToken");

      if (!user || !user.email) {
        toast.error("Admin email missing. Please login again.");
        return;
      }

      await axios.delete(`${baseUrl}/admin-api/delete-complaint/${complaint_id}`, {
        data: { adminEmail: user.email },
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      alert("Complaint deleted successfully.");
      if (isExperimental) alert("📧 Demo: Deletion email simulated.");
      navigate("/adminpage");
    } catch (error) {
      console.error("Error deleting complaint:", error);
    }
  };

  const handleBackClick = () => {
    const from = location.state?.from;
    if (from) {
      navigate(from);
    } else if (isStudent) {
      navigate("/user-analysis");
    } else if (isAssistant) {
      navigate("/assistant-dashboard");
    } else {
      navigate("/adminpage");
    }
  };

  if (!complaint) return <div className="container">Loading complaint...</div>;

  return (
    <div className="complaint-page">
      <ToastContainer />
      <div className="container">

        <button className="btn btn-outline-primary mb-3 mt-3" onClick={handleBackClick}>
          <i className="bi bi-arrow-left"></i> Back
        </button>

        <div className="complaint-header-wrapper d-flex justify-content-between align-items-center mt-4 mb-3 px-2">
          <h1 className="page-title fs-2 fw-bold mb-0">Request Details</h1>
          {isStudent && (
            <button className="btn btn-danger delete-icon-btn" onClick={handleDeleteComplaint}>
              <i className="bi bi-trash3-fill"></i>
            </button>
          )}
        </div>

        {/* MAIN SECTION */}
        <div className="content-section">

          <div className="top-row d-flex justify-content-between align-items-center position-relative">
            <span className="categoryb">
              <BiCategoryAlt size={18} /> {complaint.category}
            </span>

            <div className="engagement-box d-flex gap-3">
              <button
                className={`vote-btn vote-btn-up ${
                  userVotes[complaint.complaint_id] === "upvote" ? "active" : ""
                }`}
                onClick={() => handleVote(complaint.complaint_id, "upvote")}
              >
                <HiOutlineThumbUp size={18} />
                {complaint.likes}
              </button>

              <button
                className={`vote-btn vote-btn-down ${
                  userVotes[complaint.complaint_id] === "downvote" ? "active" : ""
                }`}
                onClick={() => handleVote(complaint.complaint_id, "downvote")}
              >
                <HiOutlineThumbDown size={18} />
                {complaint.dislikes}
              </button>
            </div>

            <div
              className="position-absolute"
              style={{ top: "10px", right: "10px", zIndex: 10 }}
            >
              {complaint?.flagged?.isFlagged ? (
                <span className="flagged-badge-red">
                  <i className="bi bi-flag-fill"></i>
                  Flagged
                </span>
              ) : !isStudent && complaint?.status !== "Resolved" ? (
                <ComplaintCategoryWithFlag
                  complaintId={complaint.complaint_id}
                  baseUrl={baseUrl}
                  complaint={complaint}
                  user={user}
                  onFlagged={() => {
                    toast.success("Complaint flagged!");
                    if (isExperimental) toast.info("📧 Demo: Flag email simulated.");
                  }}
                />
              ) : null}
            </div>
          </div>

          <h1 className="h4 fw-bold">{complaint.title}</h1>

          <div className="complaint-meta mt-2">
            <span className="me-3">
              <i className="bi bi-calendar3 me-1"></i>
              {formatDate(complaint.timestamp)}
            </span>
          </div>

          {showAdminLeaveNotice && (
            <div className="alert alert-info mt-3" role="status">
              <strong>Administrator currently unavailable</strong>
              <p className="mb-0">
                The concerned administrator is currently on leave. Your complaint has been recorded and will be resolved shortly.
                {complaint.adminLeaveToDate && (
                  <> Leave ends on {new Intl.DateTimeFormat("en-IN", { timeZone: "Asia/Kolkata" }).format(new Date(complaint.adminLeaveToDate))}.</>
                )}
              </p>
            </div>
          )}

          <p className="mt-2 description-text">{complaint.description}</p>

          {/* IT Details Section */}
          {complaint.category === "IT and Networking" && complaint.it_details && (
            <div className="it-details-section mt-4 p-4" style={{
              backgroundColor: "#f8f9ff",
              borderLeft: "4px solid #4c63d2",
              borderRadius: "8px"
            }}>
              <h5 className="fw-bold mb-3" style={{ color: "#4c63d2" }}>
                <FiWifi size={20} className="me-2" style={{ display: "inline" }} />
                IT & Networking Details
              </h5>
              <div className="row">
                {complaint.it_details.location && (
                  <div className="col-md-6 mb-2">
                    <strong className="text-muted">📍 Location:</strong>
                    <p className="ms-3">{complaint.it_details.location}</p>
                  </div>
                )}
                {complaint.it_details.connectionType && (
                  <div className="col-md-6 mb-2">
                    <strong className="text-muted">🔗 Connection Type:</strong>
                    <p className="ms-3">{complaint.it_details.connectionType}</p>
                  </div>
                )}
                {complaint.it_details.room_number && (
                  <div className="col-md-6 mb-2">
                    <strong className="text-muted">🏷️ Room Number:</strong>
                    <p className="ms-3">{complaint.it_details.room_number}</p>
                  </div>
                )}
                {complaint.it_details.internet_speed && (
                  <div className="col-md-6 mb-2">
                    <strong className="text-muted">📶 Internet Speed:</strong>
                    <p className="ms-3">{complaint.it_details.internet_speed}</p>
                  </div>
                )}
                {!isStudent && complaint.it_details.mobile_number && (
                  <div className="col-md-6 mb-2">
                    <strong className="text-muted">📞 Mobile Number:</strong>
                    <p className="ms-3">{complaint.it_details.mobile_number}</p>
                  </div>
                )}
                {complaint.it_details.issue_duration && (
                  <div className="col-md-6 mb-2">
                    <strong className="text-muted">⏱️ Issue Duration:</strong>
                    <p className="ms-3">{complaint.it_details.issue_duration}</p>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* STATUS LEFT, CATEGORY RIGHT WITH LABELS */}
          <div className="d-flex justify-content-between align-items-start mt-4 flex-wrap">

            {/* LEFT SIDE - STATUS */}
            {isStudent ? (
              <div className="form-css status-box" style={{ minWidth: "240px" }}>
                <label className="form-label fw-bold mb-1">Status</label>
                <span className={`status-pill status-${status.toLowerCase()}`}>
                  {status}
                </span>
              </div>
            ) : (
              <div className="form-css status-box" style={{ minWidth: "240px" }}>
                <label className="form-label fw-bold mb-1">Update Status</label>
                <select
                  className={`form-select ${status.toLowerCase()}-status`}
                  value={status}
                  onChange={handleStatusChange}
                  disabled={isFlagged || isNotAllowed}
                  title={isNotAllowed ? "This complaint has been reassigned to another assistant" : ""}
                >
                  <option value="Pending">⏳ Pending</option>
                  <option value="Ongoing">🔄 Ongoing</option>
                  <option value="Resolved">✅ Resolved</option>
                </select>
              </div>
            )}

            {/* RIGHT SIDE - CATEGORY */}
            {!isStudent && (
              <div className="category-box" style={{ minWidth: "280px" }}>
                <label className="form-label fw-bold mb-1">Change Category(reassign)</label>
                <select
                  className="form-select"
                  value={complaint.category}
                  onChange={handleCategoryChange}
                  disabled={isFlagged || isNotAllowed}
                  title={isNotAllowed ? "This complaint has been reassigned to another assistant" : ""}
                >
                  <option value={complaint.category}>{complaint.category} (current)</option>
                  <option value="Infrastructure">Infrastructure</option>
                  <option value="Canteen">Canteen</option>
                  <option value="Examination">Examination</option>
                  <option value="EduPrime">EduPrime</option>
                  <option value="Fee Payments and Accounts">Fee Payments and Accounts</option>
                  <option value="Academics">Academics</option>
                  <option value="Hostel Food">Hostel Food</option>
                  <option value="Extracurricular and Events">Extracurricular and Events</option>
                  <option value="Security">Security</option>
                  <option value="Sports">Sports</option>
                  <option value="Housekeeping">Housekeeping</option>
                  <option value="Audio-Visual Equipment">Audio-Visual Equipment</option>
                  <option value="Parking">Parking</option>
                  <option value="Transport">Transport</option>
                  <option value="Library">Library</option>
                  <option value="IT and Networking">IT and Networking</option>
                  <option value="Others">Others</option>
                </select>
              </div>
            )}
          </div>

          <hr className="mt-4" />

          {/* COMMENTS TIMELINE */}
          {!isStudent && (
            <>
              <h3 className="comment-heading">
                <i className="bi bi-chat-left-dots-fill"></i> Timeline ({visibleComments.length})
              </h3>

              <div className="comments-container">
                {visibleComments.length > 0 ? (
                  <div className="timeline">
                    {visibleComments.map((c, index) => {
                      const isCommentStudent = c.role === "student";
                      const roleIcon = isCommentStudent ? "🎓" : "👨‍💼";
                      const roleColor = isCommentStudent ? "#ff6b6b" : "#4c63d2";
                      const roleBgColor = isCommentStudent ? "#ffe0e0" : "#e8f0ff";

                      return (
                        <div key={c.id || index} className="timeline-item">
                          <div className="timeline-marker" style={{ borderColor: roleColor, backgroundColor: roleColor }}>
                            {roleIcon}
                          </div>
                          {index < visibleComments.length - 1 && <div className="timeline-line" style={{ borderLeftColor: roleColor }}></div>}

                          <div className="timeline-content">
                            <div className="comment-card" style={{ borderLeftColor: roleColor, backgroundColor: roleBgColor }}>
                              <div className="comment-header">
                                <div className="comment-author">
                                  <FaUser
                                    size={16}
                                    className="me-2"
                                    style={{ color: c.role === "student" ? "#ff6b6b" : "#7b1fa2" }}
                                  />
                                  <span
                                    style={{
                                      color: c.role === "student" ? "#ff6b6b" : "#7b1fa2",
                                      fontWeight: 700,
                                    }}
                                  >
                                    {c.role === "student" ? "Student" : c.email}
                                  </span>
                                </div>

                                <span className="comment-date">
                                  {formatDate(c.timestamp || c.date)}
                                </span>
                              </div>

                              <div className="comment-body">{c.text}</div>

                              {c.replies && c.replies.length > 0 && (
                                <div style={{ marginLeft: "2.4rem", marginTop: "0.6rem" }}>
                                  {(expandedReplies[c.id]
                                    ? c.replies
                                    : c.replies.slice(0, 2)
                                  ).map((r) => (
                                    <div
                                      key={r.id}
                                      style={{
                                        display: "flex",
                                        alignItems: "flex-start",
                                        gap: "10px",
                                        marginBottom: "10px",
                                      }}
                                    >
                                      <span
                                        style={{
                                          color: "#ff6b6b",
                                          fontSize: "1.3rem",
                                          marginTop: "14px",
                                        }}
                                      >
                                        ↳
                                      </span>

                                      <div
                                        style={{
                                          backgroundColor: "#f6f8fa",
                                          borderLeft: "3px solid #e2e2e2",
                                          padding: "10px 12px",
                                          borderRadius: "10px",
                                          flex: 1,
                                        }}
                                      >
                                        <strong
                                          style={{
                                            color: "#ff6b6b",
                                            fontSize: "0.9rem",
                                          }}
                                        >
                                          Student
                                        </strong>

                                        <div style={{ fontSize: "0.95rem" }}>{r.text}</div>
                                      </div>
                                    </div>
                                  ))}

                                  {c.replies.length > 2 && (
                                    <div
                                      onClick={() => toggleReplies(c.id)}
                                      style={{
                                        marginLeft: "2.6rem",
                                        marginTop: "6px",
                                        display: "flex",
                                        alignItems: "center",
                                        gap: "6px",
                                        cursor: "pointer",
                                        color: "#527dfd",
                                        fontSize: "1rem",
                                        userSelect: "none",
                                        background: "transparent",
                                      }}
                                    >
                                      <span
                                        style={{
                                          color: "#6188ff",
                                          fontSize: "1.2rem",
                                          marginTop: "2px",
                                        }}
                                      >
                                        ⤶
                                      </span>

                                      <span>
                                        {expandedReplies[c.id]
                                          ? "View less replies"
                                          : `View more replies (${c.replies.length - 2})`}
                                      </span>
                                    </div>
                                  )}
                                </div>
                              )}
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                ) : (
                  <p className="text-muted">No comments yet.</p>
                )}

                <div className="d-flex align-items-center mt-3">
                  <textarea
                    className="form-control flex-grow-1"
                    placeholder={isNotAllowed ? "This complaint has been reassigned to another assistant" : "Add your comment (minimum 4 words)..."}
                    value={newComment}
                    onChange={(e) => setNewComment(e.target.value)}
                    rows="2"
                    disabled={isNotAllowed}
                  ></textarea>

                  <button
                    className="btn btn-primary btn-sm ms-2 d-flex align-items-center justify-content-center"
                    onClick={handleCommentSubmit}
                    disabled={!newComment.trim() || isNotAllowed}
                    title={isNotAllowed ? "You cannot add comments as this complaint has been reassigned" : ""}
                    style={{ width: "60px", height: "60px" }}
                  >
                    <IoMdSend size={28} className="text-white" />
                  </button>
                </div>
              </div>
            </>
          )}

        </div>
      </div>

      {/* CENTERED RESOLUTION MODAL POPUP */}
      {showResolutionModal && (
        <div
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: "rgba(0, 0, 0, 0.55)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 1050,
            padding: "16px"
          }}
        >
          <div
            style={{
              backgroundColor: "#ffffff",
              borderRadius: "14px",
              padding: "24px 28px",
              maxWidth: "500px",
              width: "100%",
              boxShadow: "0 10px 30px rgba(0, 0, 0, 0.2)",
              animation: "fadeIn 0.2s ease-in-out"
            }}
          >
            <h5 className="fw-bold mb-2 text-dark">Resolution Summary Required</h5>
            <p className="text-secondary small mb-3">
              What action was taken to resolve this problem? (Mandatory)
            </p>

            <textarea
              className="form-control"
              rows="4"
              placeholder="Describe what was done to fix the issue (minimum 4 words)..."
              value={resolutionText}
              onChange={(e) => {
                setResolutionText(e.target.value);
                if (resolutionError) setResolutionError("");
              }}
              style={{
                borderRadius: "8px",
                borderColor: resolutionError ? "#dc3545" : "#ced4da"
              }}
            />

            {resolutionError && (
              <div className="text-danger small mt-2">
                <i className="bi bi-exclamation-circle me-1"></i>
                {resolutionError}
              </div>
            )}

            <div className="d-flex justify-content-end gap-2 mt-4">
              <button
                type="button"
                className="btn btn-outline-secondary px-4"
                onClick={handleCancelResolution}
                disabled={isSubmittingResolution}
              >
                Cancel
              </button>
              <button
                type="button"
                className="btn btn-primary px-4"
                onClick={handleConfirmResolution}
                disabled={isSubmittingResolution}
              >
                {isSubmittingResolution ? "Submitting..." : "Submit & Resolve"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Reassignment Protection Modal */}
      <ReassignmentProtectionModal
        show={showReassignmentModal}
        complaint={complaint}
        currentAssistant={reassignmentCheck.currentUser}
        assignedAssistant={reassignmentCheck.assignedTo}
        onClose={() => setShowReassignmentModal(false)}
      />

      <ToastContainer autoClose={3000} position="bottom-right" />
    </div>
  );
};

export default ComplaintsDetails;